import { type FocusEvent, type MouseEventHandler, type ReactNode, useMemo, useRef } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { get, isEmpty, isString } from 'lodash'
import { useFormContext, useWatch, type Validate } from 'react-hook-form'
import { defineMessage, useIntl } from 'react-intl'

import { useMergedRefs, useUnique } from '@subaio/components'

import { ColumnLabel, DefaultInput, FieldWrapper, HintContainer, InputError, Label, Placeholder } from '../Shared'
import { Suffix } from './Suffix'
import { Textarea } from './Textarea'

type Props = {
	label: ReactNode
	name: string

	autoComplete?: string
	isDisabled?: boolean
	isReadOnly?: boolean
	isRequired?: boolean
	maxLength?: number
	maxRows?: number
	minLength?: number
	minRows?: number
	placeholder?: string
	shouldAutofocus?: boolean
	showValidationMessage?: boolean
	validate?: Record<string, Validate<any, any>>
	hint?: ReactNode
	type?: 'email' | 'hidden' | 'number' | 'password' | 'tel' | 'text' | 'username' | 'date' | 'multiline'

	renderSuffix?: (value: any) => string
	onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
	onClick?: MouseEventHandler<HTMLInputElement | HTMLTextAreaElement>
}

export const Input = ({
	isRequired = false,
	type = 'text',
	isDisabled = false,
	minLength,
	maxLength,
	name,
	autoComplete = 'off',
	onBlur,
	onClick,
	isReadOnly = false,
	label,
	placeholder,
	shouldAutofocus,
	validate,
	minRows = 1,
	maxRows = 8,
	hint,
	showValidationMessage = true,
	renderSuffix,
	...rest
}: Props) => {
	const id = useUnique(name)
	const {
		register,
		formState: { errors },
	} = useFormContext()
	const fieldValue = useWatch({ name, defaultValue: undefined })
	const { isFocusVisible, isFocused, focusProps } = useFocusRing({ isTextInput: true, autoFocus: shouldAutofocus })
	const { hoverProps, isHovered } = useHover({})
	const inputRef = useRef<HTMLInputElement>(null)
	const { formatMessage } = useIntl()

	const inputMode = useMemo(() => {
		switch (type) {
			case 'email':
				return 'email'
			case 'hidden':
				return 'none'
			case 'number':
				return 'numeric'
			case 'tel':
				return 'tel'
			default:
				return 'text'
		}
	}, [type])
	const inputType = useMemo(() => {
		switch (type) {
			case 'number':
				return 'text'
			default:
				return type
		}
	}, [type])

	const inputProps = register(name, {
		required: isRequired
			? formatMessage(defineMessage({ id: 'formFields.input.isRequired', defaultMessage: 'This field is required' }))
			: false,
		disabled: isDisabled,
		validate,
		setValueAs: (value: unknown) => {
			const trimmedValue = typeof value === 'string' ? value.trim() : value
			if (type === 'number') {
				if (isEmpty(trimmedValue) || Number.isNaN(trimmedValue)) return undefined
				return isString(trimmedValue) && !isEmpty(trimmedValue) ? Number(trimmedValue) : undefined
			}
			return isString(trimmedValue) && isEmpty(trimmedValue) ? undefined : trimmedValue
		},
	})
	const sharedRef = useMergedRefs<HTMLInputElement | HTMLTextAreaElement>(inputProps.ref, inputRef)

	const error = get(errors, name)
	const hasError = Boolean(error)
	const defaultProps = {
		...focusProps,
		...hoverProps,

		// Avoid typing issue with react-autosize-textarea
		style: undefined,
		'aria-disabled': isReadOnly || isDisabled,
		'aria-required': isRequired,
		'aria-invalid': hasError,
		'aria-labelledby': `${id}-label`,
		'aria-describedby': `${id}-error`,
		autoFocus: shouldAutofocus,
		type: inputType,
		// Needs to be passed as both `readOnly` (for HTML) and `isReadOnly` (for styling)
		readOnly: isReadOnly,
		ref: sharedRef,
		autoComplete,
		hasError,
		id,
		inputMode,
		isDisabled,
		isFocused,
		isFocusVisible,
		isHovered,
		isReadOnly,
		maxLength,
		minLength,
		onClick,
		onBlur: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			if (onBlur) onBlur(event)
			if (focusProps.onBlur) focusProps.onBlur(event)
			return inputProps.onBlur(event)
		},
	} as const

	return (
		<FieldWrapper hidden={inputType === 'hidden'}>
			<ColumnLabel>
				{label && (
					<Label
						htmlFor={id}
						hasError={hasError}
						isFocused={isFocused && !isReadOnly && !isDisabled}
						isDisabled={isDisabled}
					>
						{label}
					</Label>
				)}
				{placeholder && (
					<Placeholder hasError={hasError} isVisible={!fieldValue && !isFocused}>
						{placeholder}
					</Placeholder>
				)}
				<div style={{ position: 'relative' }}>
					{type === 'multiline' ? (
						<Textarea
							{...inputProps}
							{...defaultProps}
							{...rest}
							ref={inputProps.ref}
							minRows={minRows}
							maxRows={maxRows}
						/>
					) : (
						<DefaultInput
							{...inputProps}
							{...defaultProps}
							{...rest}
							ref={inputProps.ref}
							hasSuffix={Boolean(renderSuffix)}
						/>
					)}
					{renderSuffix && <Suffix>{renderSuffix(fieldValue)}</Suffix>}
				</div>
				{hint && <HintContainer>{hint}</HintContainer>}
				<InputError hasError={showValidationMessage && hasError} errorMessage={error?.message as string | undefined} />
			</ColumnLabel>
		</FieldWrapper>
	)
}
